import Cookies from 'universal-cookie';
import { getSubscriptionV2 } from 'Api/AccountApi';
import { convertTimestamp } from 'Utils/Date';
import { getUser } from 'Utils/UserCookie';
import { getDefaultConfig } from 'Utils/Store';
import { FIVE_YEARS } from './Constants';

const cookies = new Cookies();

const RETRY_WAIT_TIME = 1000;

const getCookieOptions = () => {
  return { path: '/', domain: getDefaultConfig('cookieDomain') };
};

// get cookie exp from JWT
const getJWTExpiration = async () => {
  let expires;
  const currentToken = await getUser();
  if (currentToken) {
    expires = currentToken.tokenExpiration ? convertTimestamp(currentToken.tokenExpiration + FIVE_YEARS) : null;
  }
  return expires;
};

export const createEntitlementCookie = async () => {
  const { entitlementCookieName } = getDefaultConfig();
  const expires = await getJWTExpiration();
  const options = {
    ...getCookieOptions(),
    secure: true,
    sameSite: 'none',
    expires,
  };
  cookies.set(`${entitlementCookieName}-entitled`, true, options);
};

export const hasEntitlementCookie = () => {
  const { entitlementCookieName } = getDefaultConfig();
  return !!cookies.get(`${entitlementCookieName}-entitled`);
};

export const removeEntitlementCookie = () => {
  const { entitlementCookieName } = getDefaultConfig();
  cookies.remove(`${entitlementCookieName}-entitled`, getCookieOptions());
  // due to adding prefix to entitlement cookie
  // we need to remove again for users who have the previous entitlement cookie
  if (hasEntitlementCookie()) {
    cookies.remove(`${entitlementCookieName}-entitled`, { path: '/' });
  }
};

export const createMvpdSubscriptionCookie = async () => {
  const { mvpdSubscriptionCookieName } = getDefaultConfig();
  const expires = await getJWTExpiration();
  const options = {
    ...getCookieOptions(),
    expires,
  };
  cookies.set(mvpdSubscriptionCookieName, true, options);
};

export const removeMvpdSubscriptionCookie = () => {
  const { mvpdSubscriptionCookieName } = getDefaultConfig();
  cookies.remove(mvpdSubscriptionCookieName, getCookieOptions());
};

/**
 * Fetches entitlements and sets/removes based on Entitlements
 * When it is true we set entitlement Cookie
 * If a first check is failed then we try 6 times and if it is failed, clear the entitlement cookie
 * @returns {Promise<any>}  because of setInterval needs to return promise to get the Entitlements from API
 */
const wait = () => new Promise((resolve) => setTimeout(resolve, RETRY_WAIT_TIME));

// getSubscription recursive function with delay.
export const getSubscriptionWithDelay = (skus, retry = 0) => {
  // return new promise
  return new Promise(async (resolve) => {
    // remaining retries
    const retryLeft = retry - 1;
    // getSubs api call
    const {
      hasSubscription = false,
      subscriptions = [],
      hasSubscriptionV2 = false,
      subscriptionsV2 = [],
    } = await getSubscriptionV2(skus);
    // resolve if hasSubscription OR no retries left
    if (hasSubscription || retryLeft <= 0) {
      // resolve current promise, that will cause promise chain to resolve.
      return resolve({ hasSubscription, subscriptions, hasSubscriptionV2, subscriptionsV2 });
    }
    // wait RETRY_WAIT_TIME ms before retrying
    await wait();
    // retry get subscription and link/chain current promise (resolve) to the promise return by it.
    return getSubscriptionWithDelay(skus, retryLeft).then(resolve);
  });
};
